import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Typography,
  Alert,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { userService } from "@/services/api";
import { useDialog } from "@/hooks/useDialog";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { PageContainer, ToolbarContainer } from "./styles";

const DEFAULT_FORM_DATA = {
  username: "",
  password: "",
  permissions: "",
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [selectedUser, setSelectedUser] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const {
    open: openDialog,
    data: dialogData,
    handleOpen: handleOpenDialog,
    handleClose: handleCloseDialog,
  } = useDialog();

  const {
    open: openConfirm,
    data: confirmData,
    handleOpen: handleOpenConfirm,
    handleClose: handleCloseConfirm,
  } = useDialog();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await userService.getUsers(
        paginationModel.page,
        paginationModel.pageSize
      );
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      permissions: user.permissions || "",
    });
    handleOpenDialog();
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setFormData(DEFAULT_FORM_DATA);
    handleOpenDialog();
  };

  const handleSubmit = async () => {
    try {
      if (selectedUser) {
        await userService.updateUser(selectedUser.id, formData);
      } else {
        await userService.createUser(formData);
      }
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = (id) => {
    handleOpenConfirm({
      id,
      title: "確認刪除",
      message: "確定要刪除此使用者嗎？此操作無法復原。",
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await userService.deleteUser(confirmData.id);
      handleCloseConfirm();
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await userService.toggleUserStatus(user.id, !user.is_active);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "username", headerName: "使用者名稱", width: 200 },
    { field: "desc", headerName: "說明", width: 200 },
    { field: "permissions", headerName: "權限", width: 200 },
    {
      field: "is_active",
      headerName: "狀態",
      width: 130,
      renderCell: (params) => (
        <Typography color={params.value ? "primary" : "error"}>
          {params.value ? "啟用" : "停用"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "操作",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            onClick={() => handleToggleActive(params.row)}
            title={params.row.is_active ? "停用" : "啟用"}
          >
            <BlockIcon color={params.row.is_active ? "error" : "primary"} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            title="編輯"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            title="刪除"
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  if (loading && !users.length) {
    return <LoadingSpinner />;
  }

  return (
    <PageContainer>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ToolbarContainer>
        <Typography variant="h6">使用者管理</Typography>
        <Button variant="contained" color="primary" onClick={handleCreate}>
          新增使用者
        </Button>
      </ToolbarContainer>

      <DataGrid
        rows={users}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        autoHeight
        loading={loading}
        slots={{
          toolbar: GridToolbar,
        }}
        disableRowSelectionOnClick
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedUser ? "編輯使用者" : "新增使用者"}</DialogTitle>
        <DialogContent>
          {!selectedUser && (
            <TextField
              autoFocus
              margin="normal"
              label="使用者名稱"
              fullWidth
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              helperText="建立後不可修改"
            />
          )}
          {!selectedUser && (
            <TextField
              margin="normal"
              label="密碼"
              type="password"
              fullWidth
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />
          )}
          <TextField
            margin="normal"
            label="說明"
            fullWidth
            value={formData.desc}
            onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
          />
          <TextField
            margin="normal"
            label="權限"
            fullWidth
            value={formData.permissions}
            onChange={(e) =>
              setFormData({ ...formData, permissions: e.target.value })
            }
            helperText="多個權限請用逗號分隔"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleSubmit} color="primary">
            確定
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openConfirm}
        title={confirmData?.title}
        message={confirmData?.message}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseConfirm}
      />
    </PageContainer>
  );
};

export default Users;
